import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { discover as discoverApi, tags as tagsApi } from "@/lib/api";

import { QAShell } from "@/components/qa/QAShell";
import { QuestionRow } from "@/components/qa/QuestionRow";
import {
  toggleWatchedTag,
  useWatchedTags,
} from "@/components/qa/LeftNav";

/**
 * Single tag detail (`/tags/:slug`). Header with name + description +
 * watch toggle, then the same question-row list as `/questions`
 * filtered by this tag.
 */
export function TagDetail() {
  const { slug = "" } = useParams();
  const watched = useWatchedTags();
  const isWatched = watched.includes(slug);

  const tagQ = useQuery({
    queryKey: ["tag", slug],
    queryFn: () => tagsApi.get(slug),
    enabled: !!slug,
  });
  const docsQ = useQuery({
    queryKey: ["questions-by-tag", slug],
    queryFn: () =>
      discoverApi.list({ kind: "qa", tag: slug, sort: "recent", limit: 50 }),
    enabled: !!slug,
    staleTime: 30 * 1000,
  });

  if (!slug) {
    return (
      <QAShell>
        <div className="text-sm-dense text-zinc-500">Missing tag slug.</div>
      </QAShell>
    );
  }

  const tag = tagQ.data?.tag;
  const docs = docsQ.data?.documents ?? [];

  return (
    <QAShell>
      <header className="mb-4">
        <div className="flex items-baseline justify-between gap-3 flex-wrap mb-2">
          <h1 className="text-2xl font-semibold tracking-tight inline-flex items-baseline gap-2">
            <span className="inline-flex items-center text-base px-2 py-0.5 rounded bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300">
              {slug}
            </span>
          </h1>
          <button
            type="button"
            onClick={() => {
              toggleWatchedTag(slug);
              // useWatchedTags pulls on mount; quick reload to refresh.
              // Trivial v1 — a context store would be cleaner.
              window.dispatchEvent(new StorageEvent("storage"));
              window.location.reload();
            }}
            className={[
              "text-sm-dense px-3 py-1 rounded border",
              isWatched
                ? "border-accent bg-accent text-white hover:opacity-90"
                : "border-zinc-300 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-900",
            ].join(" ")}
          >
            {isWatched ? "★ Watching" : "☆ Watch tag"}
          </button>
        </div>
        {tag?.description && (
          <p className="text-sm-dense text-zinc-600 dark:text-zinc-400">
            {tag.description}
          </p>
        )}
        {tagQ.isLoading && !tag && (
          <div className="text-sm-dense text-zinc-500">Loading…</div>
        )}
        {tagQ.error && (
          <div className="text-sm-dense text-zinc-500">
            That tag doesn't exist yet.{" "}
            <Link to="/tags" className="text-accent hover:underline">
              Browse all tags
            </Link>
          </div>
        )}
      </header>
      <h2 className="text-sm-dense font-semibold uppercase tracking-wide text-zinc-500 mb-2">
        Questions
      </h2>
      {docsQ.isLoading && (
        <div className="text-sm-dense text-zinc-500 py-4">Loading…</div>
      )}
      {!docsQ.isLoading && docs.length === 0 && (
        <div className="text-center py-12 border border-dashed border-zinc-200 dark:border-zinc-800 rounded-md">
          <div className="text-sm-dense text-zinc-500 mb-3">
            No questions tagged <code>{slug}</code> yet.
          </div>
          <Link
            to="/ask"
            className="inline-block rounded-md bg-accent text-white px-4 py-2 text-sm-dense font-medium hover:opacity-90"
          >
            Ask the first one
          </Link>
        </div>
      )}
      <ul className="divide-y divide-zinc-200 dark:divide-zinc-800">
        {docs.map((d) => (
          <QuestionRow key={d.id} doc={d} />
        ))}
      </ul>
    </QAShell>
  );
}
